import type { GridApiCommunity } from '@mui/x-data-grid/internals';
import type { GridRowId } from '@mui/x-data-grid-pro';
import { Popover, PopoverAnchor, PopoverContent } from '@tempo-io/sui-popover';
import * as React from 'react';

const DEFAULT_CONTENT_CLASS_NAME = '!border-0 !rounded-none !shadow-none bg-transparent p-1';

interface CloseOnScrollOutOfView {
  api: GridApiCommunity;
  id: GridRowId;
  field: string;
}

export const EditCell = ({
  children,
  width,
  containerProps,
  contentClassName = DEFAULT_CONTENT_CLASS_NAME,
  matchCellHeight = true,
  closeOnScrollOutOfView,
}: {
  children: React.ReactNode;
  width: number;
  containerProps?: React.HTMLAttributes<HTMLDivElement> & {
    [key: `data-${string}`]: string | undefined;
  };
  contentClassName?: string;
  matchCellHeight?: boolean;
  closeOnScrollOutOfView?: CloseOnScrollOutOfView;
}) => {
  const [height, setHeight] = React.useState<number>(0);
  const resizeObserverRef = React.useRef<ResizeObserver | null>(null);
  const anchorRef = React.useRef<HTMLDivElement | null>(null);

  const contentRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }

      if (!matchCellHeight) return;

      if (node) {
        resizeObserverRef.current = new ResizeObserver((entries) => {
          for (const entry of entries) {
            setHeight(entry.contentRect.height);
          }
        });

        resizeObserverRef.current.observe(node);
        setHeight(node.offsetHeight);
      }
    },
    [matchCellHeight],
  );

  React.useEffect(() => {
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    };
  }, []);

  React.useEffect(() => {
    if (!closeOnScrollOutOfView || !anchorRef.current) return;
    const { api, id, field } = closeOnScrollOutOfView;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry && !entry.isIntersecting) {
          api.stopCellEditMode?.({ id, field, ignoreModifications: true });
        }
      },
      { threshold: 0 },
    );
    observer.observe(anchorRef.current);
    return () => observer.disconnect();
  }, [closeOnScrollOutOfView]);

  return (
    <Popover open={true}>
      <div
        className="relative self-start w-full border-0"
        style={{ height: matchCellHeight && height ? height : undefined }}
        {...containerProps}
      >
        <PopoverAnchor asChild>
          <div ref={anchorRef} className="absolute top-0 block" style={{ height: 1, width }} />
        </PopoverAnchor>
        <PopoverContent
          ref={contentRef}
          side="bottom"
          align="start"
          sideOffset={0}
          alignOffset={0}
          className={contentClassName}
          style={matchCellHeight ? { width } : undefined}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {children}
        </PopoverContent>
      </div>
    </Popover>
  );
};
